import { useNavigate } from "react-router-dom";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

interface ProjectCardProps {
  project: {
    id: string;
    thumbnail: string;
    title: string;
    year: string;
    location: string;
    description: string;
    pullQuotes?: string[];
  };
  categorySlug: string;
  index: number;
}

export default function ProjectCard({ project, categorySlug, index }: ProjectCardProps) {
  const navigate = useNavigate();
  const ref = useScrollReveal<HTMLButtonElement>({
    delay: 100 + (index % 2) * 160,
    direction: "up",
    distance: 36,
    duration: 800,
  });

  return (
    <button
      ref={ref}
      onClick={() => navigate(`/projects/${categorySlug}/${project.id}`)}
      className="group text-left bg-white rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg border border-teal/10 flex flex-col h-full"
    >
      <div className="relative h-[260px] md:h-[300px] overflow-hidden bg-charcoal shrink-0">
        <img
          src={project.thumbnail}
          alt={project.title}
          className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/5 to-black/45" />
        <div className="absolute inset-0 bg-teal/0 group-hover:bg-teal/10 transition-all duration-500" />
        <div className="absolute bottom-4 right-4 flex items-center justify-end gap-3">
          <span className="font-sans text-[10px] uppercase tracking-[2px] text-white/85 bg-black/25 backdrop-blur-sm px-3 py-1 rounded-full">
            {project.year.split(" – ")[0]}
          </span>
        </div>
      </div>
      <div className="p-6 md:p-8 flex flex-col flex-1">
        <div className="mb-5">
          <h3 className="font-serif text-xl md:text-2xl text-charcoal group-hover:text-terracotta transition-colors duration-300">
            {project.title}
          </h3>
          <p className="mt-2 font-sans text-[10px] uppercase tracking-[2px] text-terracotta">
            {project.location}
          </p>
          <div className="mt-2 w-8 h-[2px] bg-teal/40 group-hover:w-14 transition-all duration-300" />
        </div>
        <p className="font-sans text-sm text-charcoal/70 leading-relaxed line-clamp-4">
          {project.description}
        </p>
        <div className="mt-5 flex items-center gap-2 text-teal/70 group-hover:text-teal transition-colors duration-300">
          <span className="font-sans text-[11px] uppercase tracking-[2px]">View Project</span>
          <i className="ri-arrow-right-line text-xs" />
        </div>
      </div>
    </button>
  );
}